import { useTranslation } from "react-i18next"

import { TiktokOAuthStartForm } from "@/components/social-accounts/tiktok-oauth-start-form"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

/**
 * OKS-457 / OKS-466 — Floating "Continue with TikTok" bar (§01, UX
 * Spec §2.6 / §A).
 *
 * Pinned to the bottom of the viewport so the primary action is always
 * reachable without scrolling. Renders over a surface scrim with a soft
 * top shadow. Full-width primary (`accent/primary` orange = the shadcn
 * `default` Button).
 *
 * # OKS-466 — native OAuth-start form (NOT an Inertia link)
 *
 * The CTA POSTs to `/signup/tiktok/start` (`ctaUrl`) with the signed
 * invite token (`ctaToken`) as a hidden field. TikTok's authorize
 * endpoint requires a top-level cross-origin browser nav, so this is a
 * plain HTML `<form>` via `<TiktokOAuthStartForm>` (CSRF token included),
 * NEVER an Inertia XHR. The button is `type="submit"`; a local
 * `submitting` flag shows the spinner while the browser hands off.
 *
 * # Layout contract
 *
 * `fixed inset-x-0 bottom-0`. The §01 content owns the matching bottom
 * padding so the last card is never occluded (UX Spec §6).
 */
export interface FloatingCtaProps {
  /** POST action for the OAuth start (`cta_url`). When null the bar
   *  renders nothing — defensive: `valid` always ships a CTA, but a
   *  missing prop must not crash the page. */
  ctaUrl: string | null
  /** Signed invite token (`cta_token`) — hidden `token` field. */
  ctaToken: string | null
  testId?: string
}

export function FloatingCta({
  ctaUrl,
  ctaToken,
  testId = "invite-landing-floating-cta",
}: FloatingCtaProps) {
  const { t } = useTranslation()

  if (!ctaUrl) return null

  return (
    <div
      data-testid={testId}
      className={cn(
        "bg-background/95 fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur",
        "shadow-[0_-3px_12px_rgba(0,0,0,0.08)]",
      )}
    >
      <div className="mx-auto w-full px-5 py-4 sm:max-w-md">
        <TiktokOAuthStartForm
          action={ctaUrl}
          data-testid="invite-landing-cta-form"
        >
          {/* Hidden invite token — top-level (NOT user[…]). The BE
              `Signups::Tiktok::AuthorizationsController` re-validates it
              before minting OAuth state. */}
          <input
            type="hidden"
            name="token"
            value={ctaToken ?? ""}
            data-testid="invite-landing-cta-token"
          />
          <Button
            type="submit"
            size="lg"
            variant="default"
            className="min-h-[44px] w-full"
            data-testid="invite-landing-cta"
          >
            {t("invites.landing.cta.accept")}
          </Button>
        </TiktokOAuthStartForm>
      </div>
    </div>
  )
}

export default FloatingCta
